import React, { memo, useEffect, useRef, useState } from "react";
import { Box, Typography, Divider } from "@mui/material";
import { useParams } from "react-router-dom";
import { useApi } from "../../../../../../shared/hooks/useApi";
import { LoaderComponent } from "../../../../../../shared/components/loaders/page-loader";
import CustomTable from "../../../../../../shared/components/table/custom-table";
import { MRT_ColumnDef } from "material-react-table";
import { fetchIncomingTcDetail } from "./incoming-details-apis";
import { IncomingTcDetailPanelProps, IncomingTcEntry } from "./incoming-details.model";

const RenderDetailPanel = memo(({ row }: IncomingTcDetailPanelProps) => {
  const { TE_ID } = useParams();
  const { executeGet } = useApi();
  const [incomingDetail, setIncomingDetail] = useState<IncomingTcEntry | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [localLoading, setLocalLoading] = useState(false);
  const hasFetchedRef = useRef(false);

  const isExpanded = typeof row.getIsExpanded === "function" ? row.getIsExpanded() : true;

  const incomingTcId = row.original.incomingTcId 
  const productId = row.original.productId || row.original.product_id || undefined;

  useEffect(() => {
    if (!isExpanded) return; 
    if (hasFetchedRef.current) return;
    if (!TE_ID) return;
    if (!incomingTcId && !productId) {
      setError("Insufficient identifiers to fetch incoming detail.");
      return;
    }

    hasFetchedRef.current = true;
    setLocalLoading(true);
    setError(null);

    fetchIncomingTcDetail(executeGet, TE_ID, incomingTcId, productId)
      .then((data) => {
        setIncomingDetail(data);
      })
      .catch((err) => {
        console.error("Fetch error:", err);
        setError(err.message || "Failed to load details");
      })
      .finally(() => {
        setLocalLoading(false);
      });
  }, [isExpanded, incomingTcId, productId, TE_ID, executeGet]);

  if (localLoading) return <LoaderComponent />;
  if (error) return (
    <Box sx={{ width: '100%', p: 5 }}>
      <Typography color="error">{error}</Typography>
    </Box>
  );

  if (!incomingDetail) {
    if (!isExpanded) return (
      <Box sx={{ width: '100%', p: 2 }}>
        <Typography>Expand to load details</Typography>
      </Box>
    );

    return (
      <Box sx={{ width: '100%', p: 5 }}>
        <Typography>No incoming detail information available.</Typography>
      </Box>
    );
  }

  const incomingTcInfo = incomingDetail.incomingTcInfo;
  const productNetWeight = incomingDetail.productNetWeight;
  const inputTcStandard = incomingDetail.inputTcStandard || [];

  // Determine if flag seller is Yes
  const anyDetail: any = incomingDetail;
  const isFlagSellerYes = String(anyDetail.incomingTcInfo?.isFlagSeller ?? anyDetail.isFlagSeller ?? 'No') === 'Yes';

  // Data Model Relationship:
  // - inputTcStandard: Array of objects with product-specific standards
  //   [{ incomingProductArticleNo: string, incomingProductStandard: string[], ... }]
  // - tcIncomingTCStandard: Flat array of all standards (aggregated from inputTcStandard)
  //   string[] (e.g., ["GRS", "RCS"])
  // The tcIncomingTCStandard is derived by aggregating all incomingProductStandard arrays from inputTcStandard

  // Get standards value based on flag seller status
  // Uses tcIncomingTCStandard (aggregated flat array) for single value display
  const getStandardsValue = () => {
    if (isFlagSellerYes) {
      return "na";
    }
    // Use aggregated tcIncomingTCStandard from incomingTcInfo
    if (Array.isArray(incomingTcInfo.tcIncomingTCStandard) && incomingTcInfo.tcIncomingTCStandard.length > 0) {
      return incomingTcInfo.tcIncomingTCStandard.join(", ");
    }
    // Fallback: aggregate from inputTcStandard if tcIncomingTCStandard is not available
    if (inputTcStandard.length > 0) {
      const aggregated = inputTcStandard.reduce<string[]>((acc, entry) => {
        if (Array.isArray(entry.incomingProductStandard)) {
          acc.push(...entry.incomingProductStandard);
        }
        return acc;
      }, []);
      const uniqueStandards = Array.from(new Set(aggregated));
      return uniqueStandards.length > 0 ? uniqueStandards.join(", ") : "N/A";
    }
    return "N/A";
  };

  const incomingTcInfoColumns: MRT_ColumnDef<any>[] = [
    { accessorKey: "associatedProductArticleNo", header: "Associated Product Article Number", minSize: 200, maxSize: 300 },
    { accessorKey: "incomingTCNo", header: "Incoming TC Number", minSize: 150, maxSize: 250 },
    { accessorKey: "incomingATUNo", header: "Incoming ATU Number", minSize: 150, maxSize: 250 },
    { accessorKey: "incomingRMDFNo", header: "Incoming RMDF Number", minSize: 150, maxSize: 250 },
    { accessorKey: "incomingProductArticleNo", header: "Incoming Product Article Number", minSize: 200, maxSize: 400 },
  ];

  const incomingTcInfoData = [
    {
      associatedProductArticleNo: incomingTcInfo.tcAssociatedProductArticleNo || "N/A",
      incomingTCNo: incomingTcInfo.tcIncomingTCNo || "N/A",
      incomingATUNo: incomingTcInfo.tcIncomingATCNo || "N/A",
      incomingRMDFNo: incomingTcInfo.tcIncomingRMDFNo || "N/A",
      incomingProductArticleNo: Array.isArray(incomingTcInfo.tcIncomingProductNo) 
        ? incomingTcInfo.tcIncomingProductNo.join(", ") 
        : incomingTcInfo.tcIncomingProductNo || "N/A",
    },
  ];

  const productNetWeightColumns: MRT_ColumnDef<any>[] = [
    { accessorKey: "productNo", header: "Product Article Number", minSize: 150, maxSize: 250 },
    { accessorKey: "netWeight", header: "Net Weight Used", minSize: 150, maxSize: 200 },
  ];


  let productNetWeightData = Object.entries(productNetWeight.netWeights || {}).map(([productNo, netWeight]) => ({
    productNo,
    netWeight: netWeight.toFixed(2), 
  }));

  // Show empty row if no data
  if (productNetWeightData.length === 0) {
    productNetWeightData = [{ productNo: "", netWeight: "" }];
  }

  // Standard Details table configuration
  // Uses inputTcStandard (product-specific standards) to show standards per product article number
  const standardDetailsColumns: MRT_ColumnDef<any>[] = [
    { accessorKey: "incomingProductArticleNo", header: "Product Article Number", minSize: 150, maxSize: 250 },
    { 
      accessorKey: "incomingProductStandard", 
      header: "Standard(s)", 
      minSize: 150, 
      maxSize: 400,
      Cell: ({ cell }) => {
        const standards = cell.getValue() as string[];
        return Array.isArray(standards) ? standards.join(", ") : standards || "N/A";
      }
    },
  ];

  // Map inputTcStandard to table data - shows product-specific standards
  const standardDetailsData = inputTcStandard.map((item) => ({
    incomingProductArticleNo: item.incomingProductArticleNo || "N/A",
    incomingProductStandard: item.incomingProductStandard || [],
  }));

  const hasStandardDetails = standardDetailsData.length > 0;


  const rawMaterialsTableData: any[] = Object.entries(productNetWeight.rawMaterials || {}).flatMap(([productNo, rms]) => {
    if (!Array.isArray(rms)) return [];
    return rms.map((rm: any) => ({
      productNo,
      tcProductCategoryCode: rm.tcProductCategoryCode || rm.tc_product_category_code || "",
      tcProductDetailCode: rm.tcProductDetailCode || rm.tc_product_detail_code || "",
      rawMaterialCode: rm.rawMaterialCode || rm.raw_material_code || "",
      rawMaterialWeight: Number(rm.rawMaterialWeight || rm.raw_material_weight || 0),
      rawMaterialPercentage: Number(rm.rawMaterialPercentage || rm.raw_material_percentage || 0),
    }));
  });

  // Show empty row if no raw materials data
  const finalRawMaterialsTableData = rawMaterialsTableData.length === 0 
    ? [{ 
        productNo: "", 
        tcProductCategoryCode: "", 
        tcProductDetailCode: "", 
        rawMaterialCode: "", 
        rawMaterialWeight: "", 
        rawMaterialPercentage: "" 
      }] 
    : rawMaterialsTableData;

  const rawMaterialsColumns: MRT_ColumnDef<any>[] = [
    { accessorKey: "productNo", header: "Product Article Number", minSize: 150 },
    { accessorKey: "tcProductCategoryCode", header: "Product Category Code", minSize: 140 },
    { accessorKey: "tcProductDetailCode", header: "Product Detail Code", minSize: 140 },
    { accessorKey: "rawMaterialCode", header: "Raw Material Code", minSize: 140 },
    { accessorKey: "rawMaterialWeight", header: "Raw Material Weight", minSize: 120 },
    { accessorKey: "rawMaterialPercentage", header: "Raw Material %", minSize: 120 },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        p: 5,
        gap: 4,
        width: '100%',
      }}
    >
      <Box sx={{ width: '100%' }}>
        <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
          Incoming TC Information
        </Typography>
        {incomingTcInfo ? (
          <CustomTable
            columns={incomingTcInfoColumns}
            data={incomingTcInfoData}
            enableTopToolbar={false}
            enableBottomToolbar={false}
          />
        ) : (
          <Typography variant="body2" color="textSecondary">
            No incoming TC information available.
          </Typography>
        )}
      </Box>

      <Divider />

      <Box sx={{ width: '100%' }}>
        <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
          Product Net Weight Information
        </Typography>
        <CustomTable
          columns={productNetWeightColumns}
          data={productNetWeightData}
          enableTopToolbar={false}
          enableBottomToolbar={false}
        />
      </Box>


      <Box sx={{ width: '100%' }}>
      <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
              Incoming Tc Standard Information
      </Typography>
      {hasStandardDetails ? (
        <>
          <Divider />
            <CustomTable
              columns={standardDetailsColumns}
              data={standardDetailsData}
              enableTopToolbar={false}
              enableBottomToolbar={false}
            />
        </>
      ): (
        <Typography variant="body2" color="textSecondary">
          No standard data available.
        </Typography>
      )}
      </Box>

      <Box sx={{ width: '100%' }}>
      <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
              Incoming TC Raw Materials Information
            </Typography>
      <CustomTable
        columns={rawMaterialsColumns}
        data={finalRawMaterialsTableData}
        enableTopToolbar={false}
        enableBottomToolbar={false}
      />
      </Box>
    </Box>
  );
});

RenderDetailPanel.displayName = "RenderDetailPanel";

export default RenderDetailPanel;